import type { BcfComment, BcfTopic } from './bcfParser'

/** Sincronizar BCF entre quem trabalha e o servidor.
 *
 *  A escolha que está por trás disto. A plataforma é local-first: os
 *  tópicos vivem no browser e o `.bcfzip` no disco de quem o tem. Passar
 *  tudo para o servidor e acabar com isso seria muito mais simples de
 *  escrever, e seria também dizer a quem está num estaleiro sem rede que
 *  não pode responder a um conflito. Por isso há sincronização, e por
 *  isso há reconciliação.
 *
 *  **A boa notícia, e não é pequena: o BCF já resolveu metade do
 *  problema.** Cada tópico, cada comentário e cada viewpoint tem um GUID
 *  atribuído por quem o criou. Duas pessoas sem rede a criar tópicos ao
 *  mesmo tempo produzem GUIDs diferentes, portanto **criar nunca dá
 *  conflito**, e juntar é reunir os dois conjuntos. Não é preciso
 *  inventar identidade nenhuma, ao contrário do que acontece com os
 *  elementos de um IFC.
 *
 *  **A segunda boa notícia: quase tudo no BCF é só acrescentar.** Os
 *  comentários e os viewpoints não se editam na prática: acrescentam-se.
 *  Dois conjuntos de comentários juntam-se pela união dos GUIDs e ficam
 *  por ordem de data. Ninguém perde nada e não há nada a decidir.
 *
 *  **O que sobra, e é aí que está o trabalho: os campos do tópico.** O
 *  estado, a prioridade, o responsável, o título, o prazo. Se duas
 *  pessoas mudarem o MESMO campo do MESMO tópico desde o ponto em que
 *  ambas estavam de acordo, isso é um conflito a sério, e é preciso
 *  alguém decidir.
 *
 *  A regra é a mesma que já usamos no mapa de identidade da comparação
 *  e nas especificações IDS: **não se funde em silêncio**. Campos que só
 *  um lado mexeu aplicam-se sozinhos; campos que ambos mexeram param e
 *  perguntam. Uma fusão automática de um estado de conflito produz um
 *  registo limpo e falso, e o BCF é precisamente o sítio onde isso se
 *  paga caro: alguém fecha um tópico que o outro tinha reaberto e
 *  ninguém dá por nada.
 *
 *  Este ficheiro é puro. Não sabe de HTTP nem de base de dados: recebe
 *  três fotografias e diz o que fazer. É o que permite testar as
 *  decisões todas sem servidor nenhum.
 */

/** Os campos do tópico que uma pessoa edita e que, por isso, podem
 *  entrar em conflito. Os outros ou são história (quem criou, quando) ou
 *  derivam do conteúdo. */
export const CAMPOS_EDITAVEIS = [
  'title',
  'description',
  'status',
  'priority',
  'type',
  'assignedTo',
  'dueDate',
  'stage',
] as const

export type CampoEditavel = (typeof CAMPOS_EDITAVEIS)[number]

/** Uma fotografia do estado, de um dos lados. */
export interface Fotografia {
  topicos: BcfTopic[]
}

export interface ConflitoDeCampo {
  guid: string
  titulo: string
  campo: CampoEditavel
  /** O valor que ambos tinham quando estavam de acordo. */
  base: string | null
  /** O que ficou aqui. */
  meu: string | null
  /** O que está no servidor. */
  deles: string | null
}

export interface Reconciliacao {
  /** Tópicos a criar ou actualizar no lado local. */
  aplicarCa: BcfTopic[]
  /** Tópicos a enviar para o servidor. */
  enviarLa: BcfTopic[]
  /** Para cada tópico a enviar, o que o servidor tem dele (a última
   *  versão vista, ou a base quando não veio nada). Serve para enviar só
   *  o que mudou: os comentários que lá não estão, e o tópico em si só
   *  quando os campos diferem. null quando o servidor não o tem. */
  referenciaLa: Map<string, BcfTopic | null>
  /** Campos que ambos mexeram. Ninguém decide por eles. */
  conflitos: ConflitoDeCampo[]
  /** Comentários que vieram de fora e que não tínhamos. */
  comentariosNovos: number
  /** Tópicos que existem dos dois lados e que ninguém mexeu. */
  iguais: number
}

function porGuid<T extends { guid: string }>(itens: readonly T[]): Map<string, T> {
  const m = new Map<string, T>()
  for (const i of itens) m.set(i.guid, i)
  return m
}

function campo(t: BcfTopic, c: CampoEditavel): string | null {
  const v = t[c] as unknown
  return typeof v === 'string' ? v : v == null ? null : String(v)
}

/**
 * Junta dois conjuntos de comentários.
 *
 * Pela união dos GUIDs, e por ordem de data. Não há aqui conflito
 * possível: ninguém edita um comentário alheio, e dois comentários
 * escritos ao mesmo tempo por pessoas diferentes são dois comentários,
 * não duas versões de um.
 */
export function juntarComentarios(
  meus: readonly BcfComment[],
  deles: readonly BcfComment[],
  apagados: Iterable<string> = [],
): BcfComment[] {
  const fora = new Set(apagados)
  const juntos = porGuid(meus.filter((c) => !fora.has(c.guid)))
  for (const c of deles) {
    // Um comentário que já cá está não se substitui pelo de fora: se
    // alguém o editou, a data de modificação decide.
    if (fora.has(c.guid)) continue
    const meu = juntos.get(c.guid)
    if (!meu) {
      juntos.set(c.guid, c)
      continue
    }
    if (maisRecente(c.modifiedDate, meu.modifiedDate)) juntos.set(c.guid, c)
  }
  return [...juntos.values()].sort((a, b) => {
    const da = a.date ?? ''
    const db = b.date ?? ''
    if (da === db) return a.guid < b.guid ? -1 : 1
    return da < db ? -1 : 1
  })
}

/** `a` é posterior a `b`? Sem data, considera-se que não. */
function maisRecente(a: string | null, b: string | null): boolean {
  if (!a) return false
  if (!b) return true
  return a > b
}

/** Os GUID de comentários que vieram de fora e que não tínhamos. */
function contarNovos(
  meus: readonly BcfComment[],
  juntos: readonly BcfComment[],
): number {
  const tenho = new Set(meus.map((c) => c.guid))
  return juntos.filter((c) => !tenho.has(c.guid)).length
}

/** Os conjuntos do tópico que se juntam em vez de darem conflito. Os
 *  tópicos relacionados são opcionais no tópico: sem o campo, lêem-se
 *  como vazios. */
const CONJUNTOS = ['labels', 'watchers', 'viewpointRefs', 'relatedTopics'] as const
type Conjunto = (typeof CONJUNTOS)[number]

function conjunto(t: BcfTopic, c: Conjunto): string[] {
  const v = (t as unknown as Record<string, unknown>)[c]
  return Array.isArray(v) ? (v.filter((x) => typeof x === 'string') as string[]) : []
}

function mesmoConjunto(a: readonly string[], b: readonly string[]): boolean {
  const sa = new Set(a)
  const sb = new Set(b)
  if (sa.size !== sb.size) return false
  for (const x of sa) if (!sb.has(x)) return false
  return true
}

/**
 * Junta um conjunto (etiquetas, seguidores, viewpoints anexados) a três.
 *
 * Fica o que os dois têm e o que um dos lados acrescentou desde a base;
 * sai o que um dos lados tirou. Sem base, é a união: sem saber quem
 * tirou, perder o que alguém pôs é pior do que ficar com um a mais.
 */
export function juntarConjunto(
  base: readonly string[] | null,
  meu: readonly string[],
  deles: readonly string[],
): string[] {
  const m = new Set(meu)
  const d = new Set(deles)
  const b = base ? new Set(base) : null
  const out: string[] = []
  for (const x of [...meu, ...deles]) {
    if (out.includes(x)) continue
    const nosDois = m.has(x) && d.has(x)
    const acrescentado = b === null || !b.has(x)
    if (nosDois || acrescentado) out.push(x)
  }
  return out
}

function apagadosDe(t: BcfTopic | null): string[] {
  return t?.commentsApagados ?? []
}

/** Dois conjuntos de comentários são o mesmo? Pelo GUID, pela data de
 *  modificação e pelo texto. */
function mesmosComentarios(a: readonly BcfComment[], b: readonly BcfComment[]): boolean {
  if (a.length !== b.length) return false
  const mb = porGuid(b)
  for (const c of a) {
    const o = mb.get(c.guid)
    if (!o || o.modifiedDate !== c.modifiedDate || o.comment !== c.comment) return false
  }
  return true
}

/** Os campos e os conjuntos do tópico são iguais? (Sem comentários.) */
export function mesmosCampos(a: BcfTopic, b: BcfTopic): boolean {
  for (const c of CAMPOS_EDITAVEIS) if (campo(a, c) !== campo(b, c)) return false
  for (const c of CONJUNTOS) if (!mesmoConjunto(conjunto(a, c), conjunto(b, c))) return false
  return true
}

/** O tópico mudou cá desde a base? Campos, conjuntos, comentários ou
 *  comentários apagados. */
export function mudouDesdeABase(meu: BcfTopic, b: BcfTopic): boolean {
  return (
    !mesmosCampos(meu, b)
    || !mesmosComentarios(meu.comments, b.comments)
    || !mesmoConjunto(apagadosDe(meu), apagadosDe(b))
  )
}

/**
 * Reconcilia três fotografias.
 *
 * - `base`: o estado em que os dois lados estavam de acordo da última
 *   vez que se sincronizaram. É o que distingue "eu mudei" de "eles
 *   mudaram": sem ele, só se sabe que os valores diferem, e não quem o
 *   fez, e aí qualquer escolha é adivinhar.
 * - `local`: o que está aqui agora.
 * - `remoto`: o que MUDOU no servidor desde a base. O servidor só manda
 *   o que mexeu: um tópico que não vem não foi alterado lá, e isso não
 *   quer dizer que tenha sido criado cá.
 *
 * Um tópico que não está na base e só existe de um lado é criação, e
 * vai inteiro para o outro. Um tópico que está na base e não veio do
 * servidor só se envia se mudou cá. Um tópico que existe dos dois
 * lados compara-se campo a campo.
 *
 * `baseSemAcordo` dá a base de um tópico que existe dos dois lados e
 * não está na base, quando há outra razão para saber de onde os dois
 * partiram (a origem no ficheiro importado, ver `bcfOrigem.ts`). Sem
 * ela, ou se devolver null, compara-se sem base, como antes.
 */
export function reconciliar(
  base: Fotografia,
  local: Fotografia,
  remoto: Fotografia,
  opcoes: { baseSemAcordo?: (meu: BcfTopic, deles: BcfTopic) => BcfTopic | null } = {},
): Reconciliacao {
  const bases = porGuid(base.topicos)
  const locais = porGuid(local.topicos)
  const remotos = porGuid(remoto.topicos)

  const aplicarCa: BcfTopic[] = []
  const enviarLa: BcfTopic[] = []
  const referenciaLa = new Map<string, BcfTopic | null>()
  const conflitos: ConflitoDeCampo[] = []
  let comentariosNovos = 0
  let iguais = 0

  const todos = new Set([...locais.keys(), ...remotos.keys()])

  for (const guid of todos) {
    const meu = locais.get(guid) ?? null
    const deles = remotos.get(guid) ?? null

    if (meu && !deles) {
      const b = bases.get(guid) ?? null
      // Não veio do servidor: lá está como na base. Com base, só se
      // envia se mudou cá. Sem base, é criação (ou nunca houve acordo)
      // e vai inteiro.
      if (b && !mudouDesdeABase(meu, b)) {
        iguais += 1
        continue
      }
      enviarLa.push(meu)
      referenciaLa.set(guid, b)
      continue
    }
    if (!meu && deles) {
      aplicarCa.push(deles)
      comentariosNovos += deles.comments.length
      continue
    }
    if (!meu || !deles) continue
    const b = bases.get(guid) ?? opcoes.baseSemAcordo?.(meu, deles) ?? null

    // Existe dos dois lados. Os comentários juntam-se sempre, e nunca
    // são conflito; os apagados de qualquer lado saem dos dois.
    const apagados = [...new Set([...apagadosDe(meu), ...apagadosDe(deles)])]
    const comments = juntarComentarios(meu.comments, deles.comments, apagados)
    comentariosNovos += contarNovos(meu.comments, comments)

    const fundido: BcfTopic = { ...meu, comments }
    if (apagados.length > 0) fundido.commentsApagados = apagados
    let mudouCa = false
    let mudouLa = false

    for (const c of CAMPOS_EDITAVEIS) {
      const vb = b ? campo(b, c) : null
      const vm = campo(meu, c)
      const vd = campo(deles, c)
      if (vm === vd) continue

      const euMexi = vm !== vb
      const elesMexeram = vd !== vb

      if (euMexi && elesMexeram) {
        // Os dois. Ninguém decide por eles: o valor fica como está aqui
        // e o conflito sobe para quem o tem de resolver.
        conflitos.push({
          guid,
          titulo: meu.title,
          campo: c,
          base: vb,
          meu: vm,
          deles: vd,
        })
        continue
      }
      if (elesMexeram) {
        ;(fundido as unknown as Record<string, unknown>)[c] = vd
        mudouCa = true
      } else {
        mudouLa = true
      }
    }

    // Etiquetas, seguidores, viewpoints anexados e tópicos relacionados:
    // juntam-se a três. Os relacionados só se escrevem se houver algum ou
    // se o tópico de cá já tinha o campo (um tópico antigo fica igual).
    for (const c of CONJUNTOS) {
      const junto = juntarConjunto(b ? conjunto(b, c) : null, conjunto(meu, c), conjunto(deles, c))
      if (c !== 'relatedTopics' || junto.length > 0 || meu.relatedTopics !== undefined) {
        ;(fundido as unknown as Record<string, unknown>)[c] = junto
      }
      if (!mesmoConjunto(junto, conjunto(meu, c))) mudouCa = true
      if (!mesmoConjunto(junto, conjunto(deles, c))) mudouLa = true
    }
    fundido.viewpointCount = fundido.viewpointRefs.length

    if (!mesmosComentarios(comments, meu.comments)) mudouCa = true
    if (!mesmosComentarios(comments, deles.comments)) mudouLa = true
    if (!mesmoConjunto(apagados, apagadosDe(meu))) mudouCa = true
    if (!mesmoConjunto(apagados, apagadosDe(deles))) mudouLa = true

    if (mudouCa) aplicarCa.push(fundido)
    if (mudouLa) {
      enviarLa.push(fundido)
      referenciaLa.set(guid, deles)
    }
    if (!mudouCa && !mudouLa && conflitos.every((x) => x.guid !== guid)) {
      iguais += 1
    }
  }

  return { aplicarCa, enviarLa, referenciaLa, conflitos, comentariosNovos, iguais }
}

/** Resolver um conflito é escolher um dos lados. */
export type Escolha = 'meu' | 'deles'

/**
 * Aplica as escolhas a um tópico.
 *
 * Fica à parte da reconciliação de propósito: reconciliar é olhar, e
 * isto é decidir. Quem olha não deve poder decidir sem querer.
 */
export function resolver(
  topico: BcfTopic,
  conflitos: readonly ConflitoDeCampo[],
  escolhas: ReadonlyMap<CampoEditavel, Escolha>,
): BcfTopic {
  const saida = { ...topico } as unknown as Record<string, unknown>
  for (const c of conflitos) {
    if (c.guid !== topico.guid) continue
    const escolha = escolhas.get(c.campo)
    if (!escolha) continue
    saida[c.campo] = escolha === 'meu' ? c.meu : c.deles
  }
  return saida as unknown as BcfTopic
}

/** O resumo, dito a quem carregou no botão.
 *
 *  Diz também o que ficou por resolver. Um resumo que só conta o que
 *  correu bem deixa quem lê a pensar que acabou. */
export function resumo(r: Reconciliacao): string {
  const partes: string[] = []
  if (r.aplicarCa.length > 0) partes.push(`${r.aplicarCa.length} a receber`)
  if (r.enviarLa.length > 0) partes.push(`${r.enviarLa.length} a enviar`)
  if (r.comentariosNovos > 0) {
    partes.push(
      `${r.comentariosNovos} ${r.comentariosNovos === 1 ? 'comentário novo' : 'comentários novos'}`,
    )
  }
  if (r.conflitos.length > 0) {
    partes.push(
      `${r.conflitos.length} ${r.conflitos.length === 1 ? 'conflito por resolver' : 'conflitos por resolver'}`,
    )
  }
  if (partes.length === 0) return 'Nada a sincronizar.'
  return partes.join(', ') + '.'
}
